import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Heart, BookOpen, Sticker, Check } from "lucide-react";
import api, { eur } from "../api";
import { usePageSeo } from "../components/common";

const ZODIAC = ["Bélier", "Taureau", "Gémeaux", "Cancer", "Lion", "Vierge", "Balance",
  "Scorpion", "Sagittaire", "Capricorne", "Verseau", "Poissons", "Je ne sais pas"];

const INCLUS = [
  { icon: BookOpen, t: "Un verset de la Bible" },
  { icon: Heart, t: "Une guidance du mois liée à ton signe astrologique" },
  { icon: Mail, t: "Un petit mot personnel" },
  { icon: Sticker, t: "Un sticker, un dessin ou une petite attention papier" },
];

export default function EntreLuiEtMoi() {
  usePageSeo({
    title: "Entre Lui et Moi — courrier mensuel",
    description: "Entre Lui et Moi : un courrier mensuel préparé par Noëmie, entre ton cœur, Dieu et toi. Verset, guidance du mois, mot personnel et attention papier. Abonnement résiliable.",
    path: "/entre-lui-et-moi",
  });
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", date_of_birth: "", zodiac: "",
    address: "", address_complement: "", postal_code: "", city: "", country: "France",
    private_message: "",
  });

  useEffect(() => { api.get("/offers/config").then((r) => setCfg(r.data.entre_lui)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.first_name && f.last_name && f.email && f.address && f.postal_code && f.city;

  const subscribe = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Merci de remplir les champs requis."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/offers/entre-lui/checkout", { ...f, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue.");
      setLoading(false);
    }
  };

  return (
    <div className="home-dark min-h-[80vh]">
      <div className="container-x py-12 sm:py-20 fade-up">
        <div className="eyebrow mb-3 !text-[#DFC07B]">Courrier mensuel</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-medium text-[#F9F5EE]">Entre Lui et Moi</h1>
        <p className="font-script text-2xl sm:text-3xl text-[#DFC07B] mt-2">
          Un courrier rien que pour toi, entre ton cœur, Dieu et moi.
        </p>
        <div className="gold-divider my-7 w-28" />

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-3 space-y-8">
            <p className="text-[#E7DCEA] leading-relaxed text-lg">
              Chaque mois, tu reçois physiquement dans ta boîte aux lettres un courrier que je prépare avec soin,
              rien que pour toi. Un temps doux, suspendu, entre toi, ton cœur, Dieu et moi.
            </p>
            <div className="card-soft p-6 sm:p-7">
              <div className="eyebrow mb-4">Ce que contient ton courrier</div>
              <ul className="space-y-3">
                {INCLUS.map((i) => {
                  const Icon = i.icon;
                  return (
                    <li key={i.t} className="flex items-start gap-3 text-[#5A3A4B]">
                      <span className="w-9 h-9 rounded-full bg-[#C9A56A]/15 flex items-center justify-center shrink-0">
                        <Icon size={17} className="text-[#8a6a2f]" />
                      </span>
                      <span className="text-sm leading-relaxed pt-1.5">{i.t}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-5 text-xs text-[#7A5D58] italic">
                Le contenu exact peut légèrement varier chaque mois, au gré de l'inspiration et des guidances.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 text-center">
              {["Abonnement mensuel", "Résiliable à tout moment", "Aucun appel, aucune visio"].map((t) => (
                <div key={t} className="rounded-2xl border border-[#D4AF37]/25 bg-[#2B0D46]/40 p-4 text-sm text-[#E7DCEA]">
                  <Check size={16} className="mx-auto text-[#DFC07B] mb-2" />{t}
                </div>
              ))}
            </div>
          </div>

          {/* Subscription form */}
          <div className="lg:col-span-2">
            <form onSubmit={subscribe} className="card-soft p-6 sm:p-7 lg:sticky lg:top-24 space-y-4" data-testid="elm-form">
              <div className="text-center">
                <div className="font-serif text-4xl font-semibold text-[#5A3A4B]">
                  {cfg ? eur(cfg.price_cents) : "—"}<span className="text-base font-normal text-[#7A5D58]"> / mois</span>
                </div>
                <p className="text-xs text-[#7A5D58] mt-1">Prélèvement automatique chaque mois · résiliable à tout moment</p>
              </div>
              <div className="gold-divider my-2" />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="field-label">Prénom</label><input required className="field-input" value={f.first_name} onChange={set("first_name")} data-testid="elm-first" /></div>
                <div><label className="field-label">Nom</label><input required className="field-input" value={f.last_name} onChange={set("last_name")} data-testid="elm-last" /></div>
              </div>
              <div><label className="field-label">Email</label><input required type="email" className="field-input" value={f.email} onChange={set("email")} data-testid="elm-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="field-label">Date de naissance</label><input type="date" className="field-input" value={f.date_of_birth} onChange={set("date_of_birth")} data-testid="elm-dob" /></div>
                <div>
                  <label className="field-label">Signe astrologique</label>
                  <select className="field-input" value={f.zodiac} onChange={set("zodiac")} data-testid="elm-zodiac">
                    <option value="">—</option>
                    {ZODIAC.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="field-label">Adresse postale</label><input required className="field-input" value={f.address} onChange={set("address")} data-testid="elm-address" /></div>
              <div><label className="field-label">Complément d'adresse <span className="text-[#a58f92]">(facultatif)</span></label><input className="field-input" value={f.address_complement} onChange={set("address_complement")} data-testid="elm-complement" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="field-label">Code postal</label><input required className="field-input" value={f.postal_code} onChange={set("postal_code")} data-testid="elm-postal" /></div>
                <div><label className="field-label">Ville</label><input required className="field-input" value={f.city} onChange={set("city")} data-testid="elm-city" /></div>
              </div>
              <div><label className="field-label">Pays</label><input className="field-input" value={f.country} onChange={set("country")} data-testid="elm-country" /></div>
              <div>
                <label className="field-label">Y a-t-il quelque chose que tu aimerais confier à Dieu ou déposer dans ce courrier ? <span className="text-[#a58f92]">(facultatif, privé)</span></label>
                <textarea rows={3} className="field-input resize-none" value={f.private_message} onChange={set("private_message")} data-testid="elm-message" />
              </div>
              <button type="submit" disabled={loading || !valid} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="elm-subscribe">
                {loading ? "Redirection…" : "M'abonner"}
              </button>
              <p className="text-xs text-[#a58f92] text-center leading-relaxed">
                Ton adresse et ton message restent strictement privés. Paiement sécurisé par Stripe.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
